import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import FinalCTA from "@/components/FinalCTA";
import { Reveal, MaskReveal } from "@/components/Reveal";
import { WHY_ITEMS, JOURNEY } from "@/data/content";

const About = () => {
  useEffect(() => {
    document.title = "About — T Plus One | Financial Market Education";
  }, []);

  return (
    <main className="pt-32 lg:pt-40" data-testid="about-page">
      <header className="container-x pb-20 lg:pb-28 max-w-4xl">
        <Reveal><p className="eyebrow">About T Plus One</p></Reveal>
        <MaskReveal as="h1" lines={["Built to make market education", "more structured."]} className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]" />
        <Reveal delay={0.25}>
          <p className="mt-8 text-base sm:text-lg text-dim leading-relaxed max-w-2xl">
            T Plus One is an EdTech company focused on financial markets. We combine structured courses with continued community-based market learning — because understanding markets is a practice, not an event.
          </p>
        </Reveal>
      </header>

      <section className="py-24 lg:py-32 bg-white border-t border-line">
        <div className="container-x grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHeader eyebrow="Philosophy" lines={["No hype.", "No promises.", "Just structure."]} />
          </div>
          <div className="space-y-6 text-base text-dim leading-relaxed">
            <Reveal><p>The name comes from the settlement cycle — the discipline of T+1, where every trade resolves in an orderly, defined process. We believe market education should work the same way: sequenced, deliberate and accountable.</p></Reveal>
            <Reveal><p>Most people meet markets through fragments — a video here, a tip there. T Plus One was built to replace that randomness with a system: fundamentals first, then structure, then advanced frameworks, then continued practice inside a serious community.</p></Reveal>
            <Reveal><p>We do not sell dreams of financial freedom. We teach how markets work, how risk behaves, and how disciplined traders think — online and offline, across Indian equities, Forex and advanced trading concepts.</p></Reveal>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-line">
        <div className="container-x">
          <SectionHeader eyebrow="Principles" lines={["What we hold to."]} />
          <div className="mt-12 grid md:grid-cols-2 gap-x-16">
            {WHY_ITEMS.map((w, i) => (
              <Reveal key={w.title} delay={(i % 2) * 0.1}>
                <div className="py-12 border-t border-line">
                  <h3 className="text-2xl sm:text-3xl tracking-tight">{w.title}</h3>
                  <p className="mt-4 text-base text-dim leading-relaxed max-w-md">{w.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="relative bg-coal text-ivory grain py-24 lg:py-32 overflow-hidden">
        <div className="container-x">
          <SectionHeader dark eyebrow="The Method" lines={["Learn. Understand.", "Practice. Evolve."]} />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {JOURNEY.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="border-t border-coalline pt-5">
                  <p className="font-mono text-xs text-pine-2">{s.n}</p>
                  <p className="mt-2 font-medium tracking-tight">{s.title}</p>
                  <p className="mt-2 text-sm text-[#A0A09C] leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Link to="/courses" data-testid="about-courses-cta" className="link-arrow mt-14 inline-flex items-center gap-2 text-sm font-medium text-ivory hover:text-pine-2 transition-colors">
              Explore the courses <ArrowRight size={15} className="arrow" />
            </Link>
          </Reveal>
        </div>
      </section>

      <FinalCTA />
    </main>
  );
};

export default About;
